<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import PipelineStageForm from './PipelineStageForm.vue';
import PipelineForm from './PipelineForm.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedStage = ref({});
const searchQuery = ref('');
const selectedPipelineId = ref(null);
const settingsForm = ref({
  card_creation_mode: 'hybrid',
  lifecycle_settings: {
    resolve_conversation_on_close: false,
  },
});

const showAddPipelinePopup = ref(false);
const showEditPipelinePopup = ref(false);
const showDeletePipelinePopup = ref(false);
const isDeletingPipeline = ref(false);

const records = computed(() => getters['pipelineStages/getStages'].value);

const pipelines = computed(() => getters['pipelines/getPipelines'].value);

const uiFlags = computed(() => getters['pipelineStages/getUIFlags'].value);
const settings = computed(
  () => getters['crmPipelineSettings/getSettings'].value
);
const followUpRules = computed(
  () => getters['crmFollowUpRules/getRules'].value
);

const followUpForm = ref({
  name: '',
  saas_pipeline_stage_id: null,
  delay_minutes: 1440,
  message_body: '',
  enabled: true,
});

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedStage.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedStage.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteStage = async id => {
  try {
    await store.dispatch('pipelineStages/delete', {
      pipelineId: selectedPipelineId.value,
      id,
    });
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedStage.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedStage.value.id] = true;
  closeDeletePopup();
  deleteStage(selectedStage.value.id);
};

const openAddPipelinePopup = () => {
  showAddPipelinePopup.value = true;
};
const hideAddPipelinePopup = () => {
  showAddPipelinePopup.value = false;
};

const openEditPipelinePopup = () => {
  showEditPipelinePopup.value = true;
};
const hideEditPipelinePopup = () => {
  showEditPipelinePopup.value = false;
};

const openDeletePipelinePopup = () => {
  showDeletePipelinePopup.value = true;
};
const closeDeletePipelinePopup = () => {
  showDeletePipelinePopup.value = false;
};

const confirmPipelineDeletion = async () => {
  isDeletingPipeline.value = true;
  try {
    await store.dispatch('pipelines/delete', selectedPipelineId.value);
    useAlert(t('CRM.PIPELINES.SUCCESS_DELETE'));
    if (pipelines.value.length > 0) {
      selectedPipelineId.value = pipelines.value[0].id;
    } else {
      selectedPipelineId.value = null;
    }
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  } finally {
    isDeletingPipeline.value = false;
    closeDeletePipelinePopup();
  }
};

const syncSettingsForm = data => {
  if (!data) return;

  settingsForm.value = {
    card_creation_mode: data.card_creation_mode || 'hybrid',
    lifecycle_settings: {
      resolve_conversation_on_close:
        data.lifecycle_settings?.resolve_conversation_on_close || false,
    },
  };
};

const saveCrmSettings = async () => {
  try {
    const response = await store.dispatch(
      'crmPipelineSettings/update',
      settingsForm.value
    );
    syncSettingsForm(response);
    useAlert(t('CRM.SETTINGS.CRM_SETTINGS_SAVED'));
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  }
};

const resetFollowUpForm = () => {
  followUpForm.value = {
    name: '',
    saas_pipeline_stage_id: records.value[0]?.id || null,
    delay_minutes: 1440,
    message_body: '',
    enabled: true,
  };
};

const createFollowUpRule = async () => {
  if (!selectedPipelineId.value) return;

  try {
    await store.dispatch('crmFollowUpRules/create', {
      ...followUpForm.value,
      delay_minutes: Number(followUpForm.value.delay_minutes),
      saas_pipeline_id: selectedPipelineId.value,
      message_mode: 'free_form',
    });
    resetFollowUpForm();
    useAlert(t('CRM.SETTINGS.FOLLOW_UPS.CREATED'));
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  }
};

const toggleFollowUpRule = async rule => {
  try {
    await store.dispatch('crmFollowUpRules/update', {
      id: rule.id,
      enabled: !rule.enabled,
    });
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  }
};

const deleteFollowUpRule = async rule => {
  try {
    await store.dispatch('crmFollowUpRules/delete', rule.id);
    useAlert(t('CRM.SETTINGS.FOLLOW_UPS.DELETED'));
  } catch (error) {
    useAlert(error?.message || t('COMPONENTS.CODE_MESSAGE.ERROR'));
  }
};

const stageName = stageId => {
  return (
    records.value.find(stage => stage.id === stageId)?.name ||
    t('CRM.LIST.UNKNOWN')
  );
};

const tableHeaders = computed(() => {
  return [
    t('CRM.SETTINGS.TABLE_NAME'),
    t('CRM.SETTINGS.TABLE_POS'),
    t('CRM.SETTINGS.TABLE_TYPE'),
    t('CRM.SETTINGS.TABLE_COLOR'),
    t('CRM.SETTINGS.TABLE_ACTIONS'),
  ];
});

onBeforeMount(async () => {
  await store.dispatch('pipelines/get');
  const crmSettings = await store.dispatch('crmPipelineSettings/get');
  syncSettingsForm(crmSettings);
  if (pipelines.value && pipelines.value.length > 0) {
    selectedPipelineId.value = pipelines.value[0].id;
  }
});

watch(settings, syncSettingsForm);

watch(
  selectedPipelineId,
  newId => {
    if (newId) {
      store.dispatch('pipelineStages/get', newId);
      store.dispatch('crmFollowUpRules/get', { pipeline_id: newId });
    }
  },
  { immediate: true }
);

watch(records, () => {
  if (!followUpForm.value.saas_pipeline_stage_id) {
    resetFollowUpForm();
  }
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CRM.SETTINGS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CRM.SETTINGS.NO_RECORDS')"
  >
    <template #header>
      <div class="flex items-center justify-between w-full mb-4">
        <BaseSettingsHeader
          v-model:search-query="searchQuery"
          :title="$t('CRM.SETTINGS.TITLE')"
          :description="$t('CRM.SETTINGS.DESCRIPTION')"
          link-text=""
          :search-placeholder="$t('CRM.SETTINGS.SEARCH')"
          feature-name="pipelines"
          class="flex-1"
        >
          <template v-if="records?.length" #count>
            <span class="text-body-main text-n-slate-11">
              {{ $t('CRM.SETTINGS.STAGES_COUNT', { count: records.length }) }}
            </span>
          </template>
          <template #actions>
            <div class="flex items-center gap-3">
              <div
                v-if="pipelines && pipelines.length > 0"
                class="flex items-center gap-1"
              >
                <select
                  v-model="selectedPipelineId"
                  class="border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option
                    v-for="pipeline in pipelines"
                    :key="pipeline.id"
                    :value="pipeline.id"
                  >
                    {{ pipeline.name }}
                  </option>
                </select>
                <Button
                  icon="i-woot-edit-pen"
                  variant="ghost"
                  slate
                  sm
                  @click="openEditPipelinePopup"
                />
                <Button
                  v-if="pipelines.length > 1"
                  icon="i-woot-bin"
                  variant="ghost"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  @click="openDeletePipelinePopup"
                />
              </div>
              <Button
                :label="$t('CRM.PIPELINES.CREATE_TITLE')"
                variant="ghost"
                slate
                size="sm"
                icon="i-woot-plus"
                @click="openAddPipelinePopup"
              />
              <Button
                v-if="selectedPipelineId"
                :label="$t('CRM.SETTINGS.ADD_STAGE')"
                size="sm"
                @click="openAddPopup"
              />
            </div>
          </template>
        </BaseSettingsHeader>
      </div>
    </template>
    <template #body>
      <div
        class="mb-6 rounded-lg border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-4"
      >
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('CRM.SETTINGS.CRM_BEHAVIOR_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('CRM.SETTINGS.CRM_BEHAVIOR_DESCRIPTION') }}
          </p>
        </div>
        <div class="flex flex-wrap items-end gap-4">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('CRM.SETTINGS.CARD_CREATION_MODE') }}
            <select
              v-model="settingsForm.card_creation_mode"
              class="w-56 border border-n-weak bg-n-solid-1 rounded-md px-3 py-2 text-sm"
            >
              <option value="hybrid">
                {{ $t('CRM.SETTINGS.CARD_MODES.HYBRID') }}
              </option>
              <option value="conversation_auto">
                {{ $t('CRM.SETTINGS.CARD_MODES.CONVERSATION_AUTO') }}
              </option>
              <option value="manual">
                {{ $t('CRM.SETTINGS.CARD_MODES.MANUAL') }}
              </option>
            </select>
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="
                settingsForm.lifecycle_settings.resolve_conversation_on_close
              "
              type="checkbox"
              class="rounded border-n-weak"
            />
            {{ $t('CRM.SETTINGS.RESOLVE_CONVERSATION_ON_CLOSE') }}
          </label>
          <Button
            :label="$t('CRM.SETTINGS.SAVE_CRM_SETTINGS')"
            size="sm"
            @click="saveCrmSettings"
          />
        </div>
      </div>

      <div
        class="mb-6 rounded-lg border border-n-weak bg-n-solid-2 p-4 flex flex-col gap-4"
      >
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.DESCRIPTION') }}
          </p>
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-5 gap-3">
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.NAME') }}
            <input
              v-model="followUpForm.name"
              class="border border-n-weak bg-n-solid-1 rounded-md px-3 py-2 text-sm"
              type="text"
              :placeholder="$t('CRM.SETTINGS.FOLLOW_UPS.NAME_PLACEHOLDER')"
            />
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.STAGE') }}
            <select
              v-model="followUpForm.saas_pipeline_stage_id"
              class="border border-n-weak bg-n-solid-1 rounded-md px-3 py-2 text-sm"
            >
              <option
                v-for="stage in records"
                :key="stage.id"
                :value="stage.id"
              >
                {{ stage.name }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-12">
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.DELAY_MINUTES') }}
            <input
              v-model="followUpForm.delay_minutes"
              class="border border-n-weak bg-n-solid-1 rounded-md px-3 py-2 text-sm"
              min="1"
              type="number"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-sm text-n-slate-12 lg:col-span-2"
          >
            {{ $t('CRM.SETTINGS.FOLLOW_UPS.MESSAGE_BODY') }}
            <textarea
              v-model="followUpForm.message_body"
              class="min-h-10 border border-n-weak bg-n-solid-1 rounded-md px-3 py-2 text-sm"
              :placeholder="$t('CRM.SETTINGS.FOLLOW_UPS.MESSAGE_PLACEHOLDER')"
            />
          </label>
        </div>
        <div class="flex justify-end">
          <Button
            :label="$t('CRM.SETTINGS.FOLLOW_UPS.CREATE')"
            size="sm"
            :disabled="
              !followUpForm.name ||
              !followUpForm.saas_pipeline_stage_id ||
              !followUpForm.message_body
            "
            @click="createFollowUpRule"
          />
        </div>
        <div
          v-if="followUpRules.length"
          class="divide-y divide-n-weak border border-n-weak rounded-md bg-n-solid-1"
        >
          <div
            v-for="rule in followUpRules"
            :key="rule.id"
            class="flex flex-col gap-3 p-3 md:flex-row md:items-center md:justify-between"
          >
            <div class="min-w-0">
              <div class="font-medium text-sm text-n-slate-12">
                {{ rule.name }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{
                  $t('CRM.SETTINGS.FOLLOW_UPS.RULE_SUMMARY', {
                    stage: stageName(rule.saas_pipeline_stage_id),
                    delay: rule.delay_minutes,
                  })
                }}
              </div>
            </div>
            <div class="flex items-center gap-2">
              <Button
                :label="
                  rule.enabled
                    ? $t('CRM.SETTINGS.FOLLOW_UPS.DISABLE')
                    : $t('CRM.SETTINGS.FOLLOW_UPS.ENABLE')
                "
                variant="ghost"
                slate
                size="sm"
                @click="toggleFollowUpRule(rule)"
              />
              <Button
                icon="i-woot-bin"
                variant="ghost"
                slate
                sm
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="deleteFollowUpRule(rule)"
              />
            </div>
          </div>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ $t('CRM.SETTINGS.FOLLOW_UPS.EMPTY') }}
        </p>
      </div>

      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="$t('CRM.SETTINGS.NO_DATA')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="stage in items" :key="stage.id" :item="stage">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ stage.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ stage.position }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11 capitalize">
                  {{ stage.stage_type || 'active' }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <div class="flex items-center">
                  <span
                    class="w-4 h-4 ltr:mr-2 rtl:ml-2 border border-solid rounded border-n-weak"
                    :style="{ backgroundColor: stage.color }"
                  />
                  <span class="text-body-main text-n-slate-12">
                    {{ stage.color }}
                  </span>
                </div>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[stage.id]"
                    @click="openEditPopup(stage)"
                  />
                  <Button
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[stage.id]"
                    @click="openDeletePopup(stage)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <PipelineStageForm
        v-if="selectedPipelineId"
        :pipeline-id="selectedPipelineId"
        @close="hideAddPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <PipelineStageForm
        v-if="selectedPipelineId"
        :pipeline-id="selectedPipelineId"
        :selected-record="selectedStage"
        is-edit
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CRM.SETTINGS.DELETE.TITLE')"
      :message="$t('CRM.SETTINGS.DELETE.MESSAGE')"
      :message-value="selectedStage.name"
      :confirm-text="$t('CRM.SETTINGS.DELETE.CONFIRM')"
      :reject-text="$t('CRM.SETTINGS.DELETE.REJECT')"
    />

    <woot-modal
      v-model:show="showAddPipelinePopup"
      :on-close="hideAddPipelinePopup"
    >
      <PipelineForm @close="hideAddPipelinePopup" />
    </woot-modal>

    <woot-modal
      v-model:show="showEditPipelinePopup"
      :on-close="hideEditPipelinePopup"
    >
      <PipelineForm
        :selected-record="pipelines.find(p => p.id === selectedPipelineId)"
        is-edit
        @close="hideEditPipelinePopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePipelinePopup"
      :on-close="closeDeletePipelinePopup"
      :on-confirm="confirmPipelineDeletion"
      :title="$t('CRM.PIPELINES.DELETE_TITLE')"
      :message="$t('CRM.PIPELINES.DELETE.MESSAGE')"
      :confirm-text="$t('CRM.PIPELINES.DELETE.CONFIRM')"
      :reject-text="$t('CRM.PIPELINES.DELETE.REJECT')"
    />
  </SettingsLayout>
</template>
